import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── types (server: application/sales, ADR-0036). Money is in agorot / cents (integers). ── */

export type SalesRegion = "domestic" | "abroad";
export type LeadStage = "new" | "in_progress" | "won" | "lost" | "done";
export type DealChannel = "rep" | "self_service" | "partial";
export type DealStatus = "open" | "paid" | "cancelled";
export type Currency = "ILS" | "USD" | "GBP" | "EUR";

export type SalesListKey = "sales_lead_status" | "sales_lead_outcome" | "sales_payment_method" | "sales_lead_source";
export type SalesOption = { key: string; label: string; active: boolean; order: number };

export type SalesSettings = {
  vatPercent: number;
  vatPaymentMethods: string[];
  vatDomesticOnly: boolean;
  followUpDays: number;
  outcomeStages: Record<string, LeadStage>;
  noAnswerOutcomes: string[];
  defaultUsdRate: number;
};

export type ProductDto = {
  id: string;
  name: string;
  groupName: string | null;
  priceAgorot: number;
  currency: Currency;
  active: boolean;
  sortOrder: number;
  notes: string | null;
};

export type CommissionRule = {
  percent?: number;
  abroadPercent?: number;
  partialPercent?: number;
  netOfVat?: boolean;
  hourlyRate?: number;
  monthlyBase?: number;
};

export type RepDto = { id: string; name: string; userId: string | null; phone: string | null; active: boolean; commission: CommissionRule };

export type SalesMeta = {
  settings: SalesSettings;
  options: Record<SalesListKey, SalesOption[]>;
  products: ProductDto[];
  reps: RepDto[];
  listLabels: Record<SalesListKey, string>;
};

export type LeadDto = {
  id: string;
  customerId: string | null;
  name: string | null;
  phone: string | null;
  phoneNormalized: string | null;
  region: SalesRegion;
  source: string | null;
  interestProductId: string | null;
  interestProductName: string | null;
  interestText: string | null;
  statusKey: string | null;
  outcomeKey: string | null;
  stage: LeadStage;
  inquiryOn: string | null;
  listenedOn: string | null;
  followUpOn: string | null;
  lastContactOn: string | null;
  noAnswerCount: number;
  assignedRepId: string | null;
  assignedRepName: string | null;
  notes: string | null;
  details: string | null;
  listenSeconds: number;
  listenCount: number;
  lastListenAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type LeadActivity = {
  id: string;
  kind: "call" | "note" | "status" | "listen" | "deal" | "import" | "yemot";
  body: string | null;
  outcomeKey: string | null;
  actorName: string | null;
  occurredAt: string;
  metadata: Record<string, unknown>;
};

export type ListenDto = {
  id: string;
  phone?: string | null;
  extensionPath: string;
  extensionLabel: string | null;
  startedAt: string;
  seconds: number;
  source: string;
  leadId?: string | null;
  /** listens list: the lead it was attached to */
  leadName?: string | null;
  leadStage?: LeadStage | null;
};

export type LeadDetail = LeadDto & {
  activities: LeadActivity[];
  listens: ListenDto[];
  deals: Array<{ id: string; productName: string; dealOn: string; totalIlsAgorot: number; status: DealStatus }>;
};

export type LeadQuery = {
  q?: string;
  stage?: string;
  statusKey?: string;
  outcomeKey?: string;
  repId?: string;
  productId?: string;
  region?: string;
  /** today | overdue | due (today + overdue) | week */
  due?: string;
  listened?: "1";
  from?: string;
  to?: string;
  /** follow_up (default) | inquiry | listen | name | updated — sortBy / sortDir (DataTable) win */
  sort?: string;
  page?: number;
  pageSize?: number;
};

/**
 * The DataTable contract (server/src/shared/tableQuery.ts): vf_<col>, condop_/condval_/condval2_<col>,
 * sortBy / sortDir, page / limit, export=1 — sent together with the named filters.
 */
export type TableParams = Record<string, string | number | undefined>;
export type ColumnValues = { items: Array<{ value: string; count: number }> };

export type LeadQuickCounts = { all: number; today: number; overdue: number; week: number; listened: number };
export type LeadsPage = Paged<LeadDto> & { stageCounts: Record<LeadStage, number>; quickCounts: LeadQuickCounts };

export type LeadInput = Partial<{
  name: string | null;
  phone: string | null;
  source: string | null;
  interestProductId: string | null;
  interestText: string | null;
  statusKey: string | null;
  outcomeKey: string | null;
  stage: LeadStage;
  inquiryOn: string | null;
  listenedOn: string | null;
  followUpOn: string | null;
  assignedRepId: string | null;
  notes: string | null;
  details: string | null;
  customerId: string | null;
}>;

export type LeadLogInput = {
  kind: "call" | "note";
  outcomeKey?: string | null;
  statusKey?: string | null;
  body?: string | null;
  /** omit = keep, null = clear */
  followUpOn?: string | null;
  stage?: LeadStage;
};

export type DealDto = {
  id: string;
  customerId: string;
  customerName: string | null;
  customerPhone: string | null;
  leadId: string | null;
  productId: string | null;
  productName: string;
  dealOn: string;
  region: SalesRegion;
  currency: Currency;
  listPriceAgorot: number;
  discountAgorot: number;
  fxRate: number | null;
  totalIlsAgorot: number;
  vatAgorot: number;
  paidAgorot: number;
  balanceAgorot: number;
  installments: number;
  paymentMethodKey: string | null;
  channel: DealChannel;
  repId: string | null;
  repName: string | null;
  source: string | null;
  notes: string | null;
  status: DealStatus;
  createdAt: string;
};

export type DealPayment = { id: string; amountAgorot: number; paidOn: string; methodKey: string | null; note: string | null; byName: string | null };
export type DealDetail = DealDto & { payments: DealPayment[] };

export type DealQuery = {
  q?: string;
  from?: string;
  to?: string;
  productId?: string;
  repId?: string;
  channel?: string;
  region?: string;
  status?: string;
  /** "open" = only deals with a balance */
  balance?: string;
  paymentMethodKey?: string;
  customerId?: string;
  /** date (default) | oldest | amount | balance — sortBy / sortDir (DataTable) win */
  sort?: string;
  page?: number;
  pageSize?: number;
};

export type DealSums = { totalIlsAgorot: number; paidAgorot: number; balanceAgorot: number; vatAgorot: number };
export type DealsPage = Paged<DealDto> & { sums: DealSums };

export type DealInput = Partial<{
  customerId: string | null;
  customer: { name?: string | null; phone?: string | null; email?: string | null; city?: string | null; community?: string | null } | null;
  leadId: string | null;
  productId: string | null;
  productName: string | null;
  dealOn: string;
  region: SalesRegion;
  currency: Currency;
  listPriceAgorot: number;
  discountAgorot: number;
  fxRate: number | null;
  totalIlsAgorot: number | null;
  installments: number;
  paymentMethodKey: string | null;
  channel: DealChannel;
  repId: string | null;
  source: string | null;
  notes: string | null;
  payment: { amountAgorot: number; paidOn?: string | null; methodKey?: string | null; note?: string | null } | null;
  paidInFull: boolean;
}>;

export type Paged<T> = { items: T[]; total: number; page: number; pageSize: number };

export type SalesOverview = {
  period: { from: string; to: string };
  deals: { count: number; totalIlsAgorot: number; paidAgorot: number; customers: number };
  leads: {
    open: number;
    newInPeriod: number;
    wonInPeriod: number;
    conversionPct: number;
    dueToday: number;
    overdue: number;
    listenedInPeriod: number;
    stages: Partial<Record<LeadStage, number>>;
  };
  balances: { deals: number; balanceAgorot: number };
  series: Array<{ month: string; deals: number; totalIlsAgorot: number; leads: number }>;
  byProduct: Array<{ name: string; deals: number; totalIlsAgorot: number }>;
  byRep: Array<{ repId: string | null; name: string | null; deals: number; totalIlsAgorot: number }>;
  byChannel: Array<{ channel: DealChannel; deals: number; totalIlsAgorot: number }>;
  bySource: Array<{ source: string | null; leads: number; won: number }>;
  dueLeads: Array<{ id: string; name: string | null; phone: string | null; followUpOn: string | null; statusKey: string | null; outcomeKey: string | null }>;
  recentDeals: Array<{ id: string; productName: string; dealOn: string; totalIlsAgorot: number; customerName: string | null }>;
};

export type CommissionLine = {
  repId: string;
  repName: string;
  active: boolean;
  rule: CommissionRule;
  deals: number;
  domesticBaseAgorot: number;
  abroadBaseAgorot: number;
  partialBaseAgorot: number;
  commissionAgorot: number;
  monthlyBaseAgorot: number;
  totalAgorot: number;
};

export type CommissionsReport = {
  month: string;
  from: string;
  to: string;
  lines: CommissionLine[];
  partial: { deals: number; grossAgorot: number };
  selfService: { deals: number; grossAgorot: number };
  unassigned: { deals: number; grossAgorot: number };
};

export type ListensPage = Paged<ListenDto> & { byExtension: Array<{ path: string; label: string | null; listens: number; phones: number }> };

const clean = <T extends Record<string, unknown>>(q: T) =>
  Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== ""));

export const salesApi = {
  meta: () => wrap(async () => (await httpClient.get<{ item: SalesMeta }>("/sales/meta")).data.item),
  updateSettings: (patch: Partial<SalesSettings>) => wrap(async () => (await httpClient.put<{ item: SalesSettings }>("/sales/settings", patch)).data.item),
  saveOptions: (listKey: SalesListKey, options: Array<{ key?: string | null; label: string; active: boolean }>) =>
    wrap(async () => (await httpClient.put<{ items: SalesOption[] }>(`/sales/options/${listKey}`, { options })).data.items),
  overview: (q: { from?: string; to?: string } = {}) =>
    wrap(async () => (await httpClient.get<{ item: SalesOverview }>("/sales/overview", { params: clean(q) })).data.item),
  commissions: (month: string) => wrap(async () => (await httpClient.get<{ item: CommissionsReport }>("/sales/commissions", { params: { month } })).data.item),

  products: {
    list: () => wrap(async () => (await httpClient.get<{ items: ProductDto[] }>("/sales/products")).data.items),
    save: (id: string | null, body: Omit<ProductDto, "id">) =>
      wrap(async () => (id ? (await httpClient.put<{ item: ProductDto }>(`/sales/products/${id}`, body)).data.item : (await httpClient.post<{ item: ProductDto }>("/sales/products", body)).data.item)),
  },
  reps: {
    list: () => wrap(async () => (await httpClient.get<{ items: RepDto[] }>("/sales/reps")).data.items),
    save: (id: string | null, body: Omit<RepDto, "id">) =>
      wrap(async () => (id ? (await httpClient.put<{ item: RepDto }>(`/sales/reps/${id}`, body)).data.item : (await httpClient.post<{ item: RepDto }>("/sales/reps", body)).data.item)),
  },

  leads: {
    list: (q: LeadQuery & TableParams) => wrap(async () => (await httpClient.get<LeadsPage>("/sales/leads", { params: clean(q) })).data),
    /** distinct values of a column (its filter); the other filters apply */
    columnValues: (q: LeadQuery & TableParams) => wrap(async () => (await httpClient.get<ColumnValues>("/sales/leads/column-values", { params: clean(q) })).data),
    get: (id: string) => wrap(async () => (await httpClient.get<{ item: LeadDetail }>(`/sales/leads/${id}`)).data.item),
    create: (body: LeadInput) => wrap(async () => (await httpClient.post<{ item: LeadDto }>("/sales/leads", body)).data.item),
    update: (id: string, body: LeadInput) => wrap(async () => (await httpClient.put<{ item: LeadDto }>(`/sales/leads/${id}`, body)).data.item),
    log: (id: string, body: LeadLogInput) => wrap(async () => (await httpClient.post<{ item: LeadDto }>(`/sales/leads/${id}/log`, body)).data.item),
    remove: (id: string) => wrap(async () => void (await httpClient.delete(`/sales/leads/${id}`))),
    bulk: (body: { ids: string[]; assignedRepId?: string | null; stage?: LeadStage }) =>
      wrap(async () => (await httpClient.post<{ updated: number }>("/sales/leads/bulk", body)).data),
  },

  deals: {
    list: (q: DealQuery & TableParams) => wrap(async () => (await httpClient.get<DealsPage>("/sales/deals", { params: clean(q) })).data),
    columnValues: (q: DealQuery & TableParams) => wrap(async () => (await httpClient.get<ColumnValues>("/sales/deals/column-values", { params: clean(q) })).data),
    get: (id: string) => wrap(async () => (await httpClient.get<{ item: DealDetail }>(`/sales/deals/${id}`)).data.item),
    create: (body: DealInput) => wrap(async () => (await httpClient.post<{ item: DealDetail }>("/sales/deals", body)).data.item),
    update: (id: string, body: DealInput) => wrap(async () => (await httpClient.put<{ item: DealDetail }>(`/sales/deals/${id}`, body)).data.item),
    cancel: (id: string) => wrap(async () => (await httpClient.post<{ item: DealDetail }>(`/sales/deals/${id}/cancel`, {})).data.item),
    restore: (id: string) => wrap(async () => (await httpClient.post<{ item: DealDetail }>(`/sales/deals/${id}/restore`, {})).data.item),
    remove: (id: string) => wrap(async () => void (await httpClient.delete(`/sales/deals/${id}`))),
    addPayment: (id: string, body: { amountAgorot: number; paidOn?: string; methodKey?: string | null; note?: string | null }) =>
      wrap(async () => (await httpClient.post<{ item: DealDetail }>(`/sales/deals/${id}/payments`, body)).data.item),
    removePayment: (id: string, paymentId: string) =>
      wrap(async () => (await httpClient.delete<{ item: DealDetail }>(`/sales/deals/${id}/payments/${paymentId}`)).data.item),
  },

  listens: (q: { q?: string; path?: string; from?: string; to?: string; page?: number; pageSize?: number } & TableParams) =>
    wrap(async () => (await httpClient.get<ListensPage>("/sales/listens", { params: clean(q) })).data),
  listensColumnValues: (q: { q?: string; path?: string; from?: string; to?: string } & TableParams) =>
    wrap(async () => (await httpClient.get<ColumnValues>("/sales/listens/column-values", { params: clean(q) })).data),
  /** selected listens without a lead → the open lead with the phone, or a new lead */
  leadsFromListens: (ids: string[]) =>
    wrap(async () => (await httpClient.post<{ created: number; attached: number; skipped: number }>("/sales/listens/leads", { ids })).data),

  customerSales: (customerId: string) =>
    wrap(
      async () =>
        (
          await httpClient.get<{
            item: {
              deals: Array<{ id: string; productName: string; dealOn: string; totalIlsAgorot: number; paidAgorot: number; status: DealStatus; repName: string | null }>;
              leads: Array<{ id: string; stage: LeadStage; inquiryOn: string | null; statusKey: string | null; source: string | null }>;
            };
          }>(`/customers/${customerId}/sales`)
        ).data.item,
    ),
};

/* ── Yemot HaMashiach (settings) ── */

export type YemotTracked = {
  path: string;
  label: string;
  logFile: string;
  fullSeconds?: number;
  minSeconds?: number;
  createLeads?: boolean;
  leadStatusKey?: string | null;
  interestProductId?: string | null;
  enabled?: boolean;
  continueTo?: string | null;
};

export type YemotConnection =
  | { configured: false }
  | {
      configured: true;
      systemNumber: string;
      tokenMasked: string;
      hasToken: boolean;
      enabled: boolean;
      tracked: YemotTracked[];
      syncFrom: string | null;
      cursor: Record<string, { day?: string; rows?: number }>;
      lastSyncAt: string | null;
      lastSyncStatus: string | null;
      lastSyncError: string | null;
      webhookUrl: string | null;
      /** outbound caller id; null = the system number */
      callerId: string | null;
    };

export type YemotSyncSummary = {
  status: "ok" | "failed";
  filesRead: number;
  rowsRead: number;
  listensAdded: number;
  leadsCreated: number;
  leadsUpdated: number;
  errors: string[];
};

export type YemotSyncRun = {
  id: string;
  trigger: string;
  status: string;
  filesRead: number;
  rowsRead: number;
  listensAdded: number;
  leadsCreated: number;
  leadsUpdated: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
};

export type YemotBrowse = {
  path: string;
  dirs: Array<{ name: string; title: string | null }>;
  files: Array<{ name: string; size: number | null; mtime: string | null; what: string }>;
};

export type YemotPreview = {
  total: number;
  columns: string[];
  detected: { phone: string | null; date: string | null; time: string | null; seconds: string | null };
  rows: Array<Record<string, unknown>>;
  parsed: Array<{ phone: string; startedAt: string; seconds: number } | null>;
};

export const yemotApi = {
  get: () => wrap(async () => (await httpClient.get<{ item: YemotConnection }>("/settings/yemot-connection")).data.item),
  save: (body: { systemNumber: string; token?: string; authType?: "api_key" | "password"; enabled?: boolean; tracked?: YemotTracked[]; syncFrom?: string | null; callerId?: string | null }) =>
    wrap(async () => (await httpClient.put<{ item: YemotConnection }>("/settings/yemot-connection", body)).data.item),
  remove: () => wrap(async () => void (await httpClient.delete("/settings/yemot-connection"))),
  test: () =>
    wrap(async () => (await httpClient.post<{ item: { ok: boolean; name: string | null; units: string | null; username: string | null } }>("/settings/yemot-connection/test", {})).data.item),
  browse: (path: string) => wrap(async () => (await httpClient.get<{ item: YemotBrowse }>("/settings/yemot-connection/browse", { params: { path } })).data.item),
  preview: (file: string) => wrap(async () => (await httpClient.get<{ item: YemotPreview }>("/settings/yemot-connection/preview", { params: { file } })).data.item),
  sync: () => wrap(async () => (await httpClient.post<{ item: YemotSyncSummary }>("/settings/yemot-connection/sync", {})).data.item),
  runs: () => wrap(async () => (await httpClient.get<{ items: YemotSyncRun[] }>("/settings/yemot-connection/sync-runs")).data.items),
};

/* ── Yemot HaMashiach outbound (Turbo parity): tzintuk / voice message to leads, click-to-call, send history ── */

export type YemotSendKind = "tzintuk" | "voice" | "call";
export type YemotSendStatus = "sending" | "sent" | "failed";
export type YemotSkipped = { noPhone: number; abroad: number; duplicate: number; notFound: number };

export type YemotSetup = { configured: boolean; myPhone: string | null; callerId: string | null };

export type YemotSendPreview = {
  kind: "tzintuk" | "voice";
  count: number;
  max: number;
  skipped: YemotSkipped;
  sample: Array<{ name: string | null; phone: string }>;
  callerId: string | null;
  /** units left on the line (GetSession); null when Yemot did not say */
  units: number | null;
};

export type YemotLiveCounts = { pending: number; ringing: number; done: number; failed: number; bridged: number; redial: number; blocked: number; total?: number };
export type YemotLastStatus = { campaignStatus?: string | null; liveCounts?: YemotLiveCounts | null; synced?: number; pending?: boolean; hint?: string; error?: string };

export type YemotSendDto = {
  id: string;
  kind: YemotSendKind;
  title: string;
  messageText: string | null;
  callerId: string | null;
  status: YemotSendStatus;
  recipientCount: number;
  skipped: Partial<YemotSkipped>;
  yemotCampaignIds: string | null;
  dial: { callsCount?: number | null; errors?: Record<string, string>; from?: string };
  lastStatus: YemotLastStatus | null;
  lastStatusAt: string | null;
  error: string | null;
  createdByName: string | null;
  createdAt: string;
};

export type YemotSendDetail = YemotSendDto & {
  recipients: Array<{ id: string; leadId: string | null; name: string | null; phone: string; yemotStatus: string | null; dialError: string | null }>;
  statusSummary: { byStatus: Record<string, number>; answered: number; unanswered: number; unknown: number };
  refreshed: YemotLastStatus | null;
};

export const yemotOutboundApi = {
  setup: () => wrap(async () => (await httpClient.get<{ item: YemotSetup }>("/sales/yemot/setup")).data.item),
  preview: (body: { kind: "tzintuk" | "voice"; leadIds: string[] }) =>
    wrap(async () => (await httpClient.post<{ item: YemotSendPreview }>("/sales/yemot/sends/preview", body)).data.item),
  send: (body: { kind: "tzintuk" | "voice"; leadIds: string[]; title?: string | null; text?: string | null; callerId?: string | null }) =>
    wrap(
      async () =>
        (await httpClient.post<{ item: { id: string; count: number; skipped: YemotSkipped; failed: number; campaignIds: string[]; message: string } }>("/sales/yemot/sends", body)).data
          .item,
    ),
  list: (q: { page?: number; limit?: number; kind?: YemotSendKind | "" } = {}) =>
    wrap(
      async () =>
        (await httpClient.get<{ items: YemotSendDto[]; total: number; page: number; pageSize: number; byKind: Partial<Record<YemotSendKind, number>> }>("/sales/yemot/sends", { params: clean(q) }))
          .data,
    ),
  get: (id: string, refresh = false) =>
    wrap(async () => (await httpClient.get<{ item: YemotSendDetail }>(`/sales/yemot/sends/${id}`, { params: refresh ? { refresh: 1 } : {} })).data.item),
  call: (body: { leadId: string; fromPhone?: string | null; remember?: boolean }) =>
    wrap(async () => (await httpClient.post<{ item: { id: string; fromPhone: string; message: string } }>("/sales/yemot/call", body)).data.item),
};
